import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { ApiError, PaginatedApiResponse, ApiResponse } from "@/lib/apiClient";
import { 
  BaseEntity, 
  CrudService, 
  CrudFilters, 
  FetchOptions, 
  SearchOptions, 
  CreateRequest, 
  BulkUpdateRequest, 
  BulkDeleteRequest 
} from "@/types/crud";
import { CrudSliceReturn } from "@/utils/createCrudSlice";

export interface CreateCrudSagaConfig<T extends BaseEntity, TFilters extends CrudFilters = CrudFilters> {
  service: CrudService<T, TFilters>;
  actions: CrudSliceReturn<T, TFilters>['actions'];
  entityName: string; // For error messages
}

export const createCrudSagas = <T extends BaseEntity, TFilters extends CrudFilters = CrudFilters>(
  config: CreateCrudSagaConfig<T, TFilters>
) => {
  const { service, actions, entityName } = config;

  // Create a generic error handler
  const handleError = (error: unknown, fallbackMessage: string) => {
    if (error instanceof ApiError) {
      return error;
    } else {
      return new ApiError(
        500,
        "UNKNOWN_ERROR",
        (error as any)?.message || fallbackMessage,
      );
    }
  };

  // List operations
  function* fetchSaga(action: PayloadAction<FetchOptions<TFilters>>) {
    try {
      const response: PaginatedApiResponse<T[]> = yield call(service.getAll, action.payload);
      yield put(
        actions.fetchSuccess({
          list: response.data,
          page: response.pagination.page,
          total: response.pagination.total,
          pageSize: response.pagination.pageSize,
          totalPages: response.pagination.totalPages,
        }),
      );
    } catch (error: unknown) {
      yield put(actions.fetchFailure(handleError(error, `Failed to fetch ${entityName} list`)));
    }
  }

  // Single item operations
  function* fetchItemSaga(action: PayloadAction<{ id: string }>) {
    try {
      const { id } = action.payload;
      const response: ApiResponse<T> = yield call(service.getById, id);
      yield put(actions.fetchItemSuccess({ item: response.data }));
    } catch (error: unknown) {
      yield put(actions.fetchItemFailure(handleError(error, `Failed to fetch ${entityName}`)));
    }
  }

  // Create operations
  function* createSaga(action: PayloadAction<CreateRequest<T>>) {
    try {
      const response: ApiResponse<T> = yield call(service.create, action.payload);
      yield put(actions.createSuccess({ item: response.data }));
    } catch (error: unknown) {
      yield put(actions.createFailure(handleError(error, `Failed to create ${entityName}`)));
    }
  }

  // Update operations
  function* updateSaga(action: PayloadAction<{ id: string; updates: Partial<T> }>) {
    try {
      const { id, updates } = action.payload;
      const response: ApiResponse<T> = yield call(service.update, id, updates);
      yield put(actions.updateSuccess({ item: response.data }));
    } catch (error: unknown) {
      yield put(actions.updateFailure(handleError(error, `Failed to update ${entityName}`)));
    }
  }

  // Delete operations
  function* deleteSaga(action: PayloadAction<{ id: string }>) {
    try {
      const { id } = action.payload;
      yield call(service.delete, id);
      yield put(actions.deleteSuccess({ id }));
    } catch (error: unknown) {
      yield put(actions.deleteFailure(handleError(error, `Failed to delete ${entityName}`)));
    }
  }

  // Search operations
  function* searchSaga(action: PayloadAction<SearchOptions<TFilters>>) {
    try {
      const response: PaginatedApiResponse<T[]> = yield call(service.search, action.payload);
      yield put(
        actions.searchSuccess({
          list: response.data,
          page: response.pagination.page,
          total: response.pagination.total,
          pageSize: response.pagination.pageSize,
          totalPages: response.pagination.totalPages,
        }),
      );
    } catch (error: unknown) {
      yield put(actions.searchFailure(handleError(error, `Failed to search ${entityName}`)));
    }
  }

  // Bulk operations
  function* bulkUpdateSaga(action: PayloadAction<BulkUpdateRequest>) {
    try {
      if (!service.bulkUpdate) {
        throw new Error(`Bulk update not supported for ${entityName}`);
      }
      const response: ApiResponse<{ updatedItems: T[] }> = yield call(service.bulkUpdate, action.payload);
      yield put(actions.bulkUpdateSuccess({ updatedItems: response.data.updatedItems }));
    } catch (error: unknown) {
      yield put(actions.bulkUpdateFailure(handleError(error, `Failed to bulk update ${entityName}`)));
    }
  }

  function* bulkDeleteSaga(action: PayloadAction<BulkDeleteRequest>) {
    try {
      if (!service.bulkDelete) {
        throw new Error(`Bulk delete not supported for ${entityName}`);
      }
      yield call(service.bulkDelete, action.payload);
      yield put(actions.bulkDeleteSuccess({ deletedIds: action.payload.ids }));
    } catch (error: unknown) {
      yield put(actions.bulkDeleteFailure(handleError(error, `Failed to bulk delete ${entityName}`)));
    }
  }

  // Root saga generator
  function* rootSaga() {
    yield takeLatest(actions.fetchRequest.type, fetchSaga);
    yield takeLatest(actions.fetchItemRequest.type, fetchItemSaga);
    yield takeEvery(actions.createRequest.type, createSaga);
    yield takeEvery(actions.updateRequest.type, updateSaga);
    yield takeEvery(actions.deleteRequest.type, deleteSaga);
    yield takeLatest(actions.searchRequest.type, searchSaga);
    yield takeEvery(actions.bulkUpdateRequest.type, bulkUpdateSaga);
    yield takeEvery(actions.bulkDeleteRequest.type, bulkDeleteSaga);
  }

  return {
    rootSaga,
    sagas: {
      fetchSaga,
      fetchItemSaga,
      createSaga,
      updateSaga,
      deleteSaga,
      searchSaga,
      bulkUpdateSaga,
      bulkDeleteSaga,
    },
  };
};

export type CrudSagasReturn<T extends BaseEntity, TFilters extends CrudFilters = CrudFilters> = ReturnType<typeof createCrudSagas<T, TFilters>>;
